import axios from 'axios';

type CmsPopulation = {
  [field: string]: true | CmsPopulation;
};

// Keep population explicit so Strapi only returns the nested content Ombak uses.
const CMS_POPULATION = {
  'about-us': {
    ombak: true,
    mesn: true,
    mesnList: { image: true },
    research: true,
    researchList: { image: true },
    team: true
  },
  'access-data': {
    image: true,
    hyperLinks: true
  },
  articles: {
    headerImage: true,
    bodyImage1: true,
    bodyImage2: true,
    bodyImage3: true
  },
  collaborate: {
    image: true
  },
  'contact-us': {},
  'contribute-data': {
    image: true,
    hyperLinks: true
  },
  'data-use-policy': {},
  faq: {
    general: true,
    data: true,
    test: true
  },
  footer: {},
  highlight: {},
  homepage: {
    main: true,
    buoy: true,
    buoyCard: { imageList: { image: true } },
    graph: true,
    icon: { image: true },
    logo: true,
    announcement: true
  },
  mma: {
    image: true,
    overview: true,
    speakers: { image: true },
    downloads: { links: true },
    livestream: {
      lecture: true,
      qNaLink: true
    },
    aboutSingapore: {
      gettingAroundDescription: true,
      suggestedAttractionsLinks: true,
      diveOperators: true
    }
  },
  'other-resource': {
    singapore: true,
    asia: { list: { links: true } },
    global: { list: { links: true } }
  },
  'our-buoy': {
    buoy: true,
    capability: true,
    capabilityList: { image: true },
    station: true,
    stationList: { imageList: { image: true } },
    buoyDesignDescription: true
  },
  'our-water': {
    sectionList: { gif: true }
  },
  parameter: {
    parameter: true
  },
  publication: {
    list: { publications: true }
  },
  'sensor-detail': {
    sensors: true
  },
  teams: {
    image: true
  }
} as const satisfies Record<string, CmsPopulation>;

export type CmsRoute = keyof typeof CMS_POPULATION;

const strapiResponseFormat = import.meta.env.VITE_STRAPI_RESPONSE_FORMAT;
let useStrapiResponseFormat = false;

const cms = axios.create({
  baseURL: import.meta.env.VITE_CMS_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

cms.interceptors.request.use((config) => {
  if (strapiResponseFormat && useStrapiResponseFormat) {
    config.headers.set('Strapi-Response-Format', strapiResponseFormat);
  }

  return config;
});

cms.interceptors.response.use((response) => {
  if (
    !strapiResponseFormat ||
    response.config.headers.has('Strapi-Response-Format') ||
    !hasFlattenedStrapiData(response.data)
  ) {
    return response;
  }

  // Retry the first flattened v5 response in v4 format, then use that format
  // for subsequent CMS requests while Ombak still relies on data.attributes.
  useStrapiResponseFormat = true;
  return cms.request(response.config);
});

export function getCmsApiPath(route: CmsRoute, params: string[] = []) {
  const query = new URLSearchParams();

  appendPopulation(query, 'populate', CMS_POPULATION[route]);

  for (const param of params) {
    const extraParams = new URLSearchParams(param);
    extraParams.forEach((value, key) => query.append(key, value));
  }

  const queryString = query.toString();
  return `/api/${route}${queryString ? `?${queryString}` : ''}`;
}

export function getCmsIdentifierFilter(identifier: string) {
  const identifierField = /^\d+$/.test(identifier) ? 'id' : 'documentId';
  return `filters[${identifierField}][$eq]=${identifier}`;
}

function appendPopulation(
  query: URLSearchParams,
  prefix: string,
  population: CmsPopulation
) {
  for (const [field, nestedPopulation] of Object.entries(population)) {
    const fieldPrefix = `${prefix}[${field}]`;

    if (nestedPopulation === true) {
      query.append(fieldPrefix, 'true');
      continue;
    }

    appendPopulation(
      query,
      `${fieldPrefix}[populate]`,
      nestedPopulation as CmsPopulation
    );
  }
}

function hasFlattenedStrapiData(responseBody: unknown) {
  if (!responseBody || typeof responseBody !== 'object') {
    return false;
  }

  const responseData = (responseBody as { data?: unknown }).data;
  const entity = Array.isArray(responseData) ? responseData[0] : responseData;

  return Boolean(
    entity &&
      typeof entity === 'object' &&
      'documentId' in entity &&
      !('attributes' in entity)
  );
}

export default cms;
